import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bug, ThumbsUp, X, ChatTeardropDots, Flask } from "@phosphor-icons/react";

// Beta starts on first deploy — update BETA_START_ISO when you go live.
// The "Xd left" chip auto-counts down; falls back to "still gathering feedback"
// once the 60-day window has elapsed.
const BETA_START_ISO = "2026-02-15T00:00:00Z";
const BETA_DAYS = 60;

const ACK_KEY = "nk_beta_ack_v1";

/** Sticky banner shown on the Landing while the app is in beta.
 *  Players can thumbs-up the notice (dismisses for them) or open a
 *  feedback modal to send bug reports / ideas.
 */
export default function TestModeBanner() {
  const [open, setOpen] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [acked, setAcked] = useState(() => {
    try { return !!localStorage.getItem(ACK_KEY); } catch { return false; }
  });
  const [daysLeft, setDaysLeft] = useState(BETA_DAYS);

  useEffect(() => {
    const start = new Date(BETA_START_ISO).getTime();
    const end = start + BETA_DAYS * 86400 * 1000;
    const remaining = Math.max(0, Math.ceil((end - Date.now()) / (86400 * 1000)));
    setDaysLeft(remaining);
  }, []);

  if (!open || acked) return null;

  const thumbsUp = async () => {
    try {
      localStorage.setItem(ACK_KEY, "1");
    } catch { /* ignore */ }
    setAcked(true);
    try { await api.post("/feedback/ack"); } catch { /* fire-and-forget */ }
    toast.success("Thanks — happy testing!", { duration: 2500 });
  };

  return (
    <>
      <div
        className="sticky top-0 z-40 bg-gradient-to-r from-nk-accent/10 via-nk-gold/5 to-nk-accent/10 border-b border-nk-gold/30 backdrop-blur-md"
        data-testid="beta-banner"
        role="region"
        aria-label="Beta test mode notice"
      >
        <div className="max-w-[1400px] mx-auto px-4 py-2.5 sm:py-3 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 shrink-0">
            <Flask size={14} weight="fill" className="text-nk-gold" />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-nk-gold">
              Test Mode
            </span>
            {daysLeft > 0 ? (
              <span className="text-[10px] text-nk-dim font-mono">· {daysLeft}d left</span>
            ) : (
              <span className="text-[10px] text-nk-dim font-mono">· still gathering feedback</span>
            )}
          </div>

          <p className="text-xs sm:text-sm text-nk-text flex-1 min-w-[200px] leading-snug">
            NKognito is in a <span className="text-nk-gold font-medium">60-day public beta</span>.
            Prize money is <strong className="text-nk-text">not yet active</strong> — we&apos;re polishing the arena with real testers. Spot a bug? Share it below.
          </p>

          <div className="flex items-center gap-1.5 shrink-0 ml-auto">
            <button
              onClick={thumbsUp}
              data-testid="beta-thumbs-up"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-nk-accent text-nk-bg text-xs font-medium rounded-sm hover:bg-nk-accent/90 transition-colors"
              title="I get it — let me in"
            >
              <ThumbsUp size={12} weight="fill" /> Got it
            </button>
            <button
              onClick={() => setShowForm(true)}
              data-testid="beta-feedback-open"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-nk-gold/50 text-nk-gold text-xs font-medium rounded-sm hover:bg-nk-gold/10 transition-colors"
            >
              <Bug size={12} weight="fill" /> Report
            </button>
            <button
              onClick={() => setOpen(false)}
              data-testid="beta-banner-close"
              aria-label="Close beta notice"
              className="p-1.5 text-nk-mute hover:text-nk-text"
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {showForm && <FeedbackDialog onClose={() => setShowForm(false)} />}
    </>
  );
}

function FeedbackDialog({ onClose }) {
  const [form, setForm] = useState({
    category: "bug",
    severity: "medium",
    title: "",
    message: "",
    email: "",
    contact_back: false,
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        category: form.category,
        severity: form.severity,
        title: form.title,
        message: form.message,
        contact_back: form.contact_back,
        page: typeof window !== "undefined" ? window.location.pathname : "/",
      };
      if (form.email && form.email.trim()) payload.email = form.email.trim();
      await api.post("/feedback/submit", payload);
      setSent(true);
      toast.success("Thanks — your feedback made it through.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail) ? detail.map(d => d.msg || "").join("; ") : "Couldn't send. Try again?";
      toast.error(msg || "Couldn't send. Try again?");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
         data-testid="beta-feedback-dialog">
      <div className="w-full max-w-lg bg-nk-bg border border-nk-gold/40 rounded-sm">
        <div className="p-5 border-b border-nk-line/40 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChatTeardropDots size={16} weight="duotone" className="text-nk-gold" />
            <p className="font-display text-lg font-medium">Tell us what broke</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="beta-feedback-close"
                  className="p-1.5 text-nk-mute hover:text-nk-text">
            <X size={14} weight="bold" />
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center space-y-3">
            <ThumbsUp size={32} weight="fill" className="text-nk-accent mx-auto" />
            <p className="font-display text-2xl">Thank you 🙏</p>
            <p className="text-sm text-nk-mute max-w-sm mx-auto">
              We read every report. The fastest fixes ship within 24 hours.
            </p>
            <button onClick={onClose}
                    className="mt-3 px-4 py-2 text-sm border border-nk-line text-nk-mute hover:text-nk-text rounded-sm">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <select value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        data-testid="feedback-category"
                        className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none">
                  <option value="bug">Bug — something broke</option>
                  <option value="feature">Feature request</option>
                  <option value="idea">Idea / suggestion</option>
                  <option value="general">General feedback</option>
                </select>
              </Field>
              <Field label="Severity">
                <select value={form.severity}
                        onChange={(e) => setForm({ ...form, severity: e.target.value })}
                        data-testid="feedback-severity"
                        className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none">
                  <option value="low">Low · nice to fix</option>
                  <option value="medium">Medium · annoying</option>
                  <option value="high">High · blocks me</option>
                  <option value="blocker">Blocker · app unusable</option>
                </select>
              </Field>
            </div>

            <Field label="Title">
              <input value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder="Short summary, e.g. 'NK Daily question 7 had two right answers'"
                     required minLength={3} maxLength={140}
                     data-testid="feedback-title"
                     className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none" />
            </Field>

            <Field label="Describe the issue *">
              <textarea value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Steps to reproduce, what you expected, what actually happened. Screenshots welcome — paste a link."
                        required minLength={5} maxLength={4000} rows={5}
                        data-testid="feedback-message"
                        className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none resize-y" />
              <p className="text-[10px] text-nk-dim text-right">{form.message.length}/4000</p>
            </Field>

            <Field label="Your email (optional — only if you want us to reply)">
              <input type="email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     placeholder="you@example.com"
                     data-testid="feedback-email"
                     className="w-full bg-nk-bg border border-nk-line px-3 py-2 text-sm rounded-sm focus:border-nk-gold outline-none" />
            </Field>

            {form.email && (
              <label className="flex items-center gap-2 text-xs text-nk-mute cursor-pointer">
                <input type="checkbox" checked={form.contact_back}
                       onChange={(e) => setForm({ ...form, contact_back: e.target.checked })}
                       data-testid="feedback-contact-back" />
                It&apos;s OK to email me with follow-up questions
              </label>
            )}

            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose}
                      className="px-4 py-2 text-sm border border-nk-line text-nk-mute hover:text-nk-text rounded-sm">
                Cancel
              </button>
              <button type="submit" disabled={busy}
                      data-testid="feedback-submit"
                      className="flex-1 px-4 py-2 bg-nk-gold text-nk-bg font-medium text-sm rounded-sm hover:bg-nk-gold/90 disabled:opacity-40 inline-flex items-center justify-center gap-1.5">
                {busy ? "Sending…" : "Send to the team"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-nk-mute block mb-1">{label}</span>
      {children}
    </label>
  );
}
